import { useState, useEffect, createContext, useContext, useCallback } from "react";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_KEY = "bc_access_session";

export const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);

export function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}
export function saveSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}
export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

export default function AccessGate({ children }) {
  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [session, setSession] = useState(null);
  const [kickedReason, setKickedReason] = useState(null); // "expired" | "kicked" | null

  // New email/password auth — if the user is logged in via JWT, they
  // automatically get past the legacy access-code gate.
  const { isAuthenticated, loading: authLoading } = useAuth();

  const verify = useCallback(async () => {
    const stored = getSession();
    if (!stored) { setStatus("invalid"); return; }
    try {
      const body = stored.type === "admin"
        ? { admin_token: stored.token }
        : { code: stored.code, device_id: stored.device_id };
      const res = await axios.post(`${API}/auth/check`, body);
      if (res.data.valid) {
        setSession(stored);
        setStatus("valid");
      } else {
        const reason = res.data.reason || null;
        clearSession();
        setKickedReason(reason);
        setSession(null);
        setStatus("invalid");
      }
    } catch {
      // Network error: trust cached session temporarily
      setSession(stored);
      setStatus("valid");
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  // Continuous validation while logged-in: poll every 60s + re-verify when
  // the tab comes back to focus, so an expired (or revoked) code kicks the
  // user out within at most 60 seconds, immediately on tab return.
  useEffect(() => {
    if (status !== "valid") return undefined;
    const id = setInterval(() => { verify(); }, 60_000);
    const onVisible = () => { if (document.visibilityState === "visible") verify(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status, verify]);

  const onLogin = (sessionData) => {
    saveSession(sessionData);
    setSession(sessionData);
    setKickedReason(null);
    setStatus("valid");
  };

  const onLogout = () => {
    clearSession();
    setSession(null);
    setKickedReason(null);
    setStatus("invalid");
  };

  if (status === "checking" || authLoading) {
    return (
      <div style={{ background: "var(--bc-bg, var(--bc-bg, #020202))", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 32, height: 32, border: "2px solid rgba(34,197,94,0.2)", borderTop: "2px solid #22C55E", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // Bypass gate when the user is logged-in via the new email/password
  // system (JWT). They are either a paying customer, on trial, or a
  // free-tier user — and feature gating happens per-feature, not here.
  if (isAuthenticated) {
    return (
      <SessionContext.Provider value={{ session: null, onLogout: () => {} }}>
        {children}
      </SessionContext.Provider>
    );
  }

  if (status === "invalid") {
    return <LoginPage onLogin={onLogin} kickedReason={kickedReason} />;
  }

  return (
    <SessionContext.Provider value={{ session, onLogout }}>
      {children}
    </SessionContext.Provider>
  );
}
